import { useCallback, useEffect, useState } from "react";
import { UserCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/PersonCard";
import { years } from "@/lib/tree";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export const ClaimSection = ({ persons, onClaimed }) => {
  const [mine, setMine] = useState(undefined);
  const [pick, setPick] = useState("");
  const load = useCallback(() => api.get("/claims").then(({ data }) => setMine(data.find((c) => c.mine) || null)), []);
  useEffect(() => { load(); }, [load]);
  const open = persons.filter((p) => !p.has_account).sort((a, b) => a.name.localeCompare(b.name));

  const claim = async () => {
    try {
      const { data } = await api.post(`/persons/${pick}/claim`);
      toast.success(data.status === "approved" ? "Profile linked to your account" : "Claim sent — awaiting 3 family approvals");
      data.status === "approved" ? onClaimed() : load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (mine === undefined) return null;
  return (
    <section className="rounded-3xl border border-[#E5DEC9] bg-white p-7 space-y-4" data-testid="claim-section">
      <div className="flex items-center gap-3"><UserCheck className="w-5 h-5 text-[#1E3A2B]" /><h2 className="font-serif text-2xl font-semibold">{t("Already on the tree?")}</h2></div>
      {mine ? (
        <div className="flex items-center gap-4" data-testid="claim-pending">
          <Avatar p={{ name: mine.person_name, photo_url: mine.photo_url, gender: mine.gender }} size="w-12 h-12" />
          <p className="text-[#52635B]"><Clock className="inline w-4 h-4 mr-1 text-[#F57F17]" />Your claim for <b>{mine.person_name}</b> is waiting — {mine.approvals}/3 approvals.</p>
        </div>
      ) : open.length === 0 ? (
        <p className="text-sm text-[#7E8E85]">No unclaimed profiles right now.</p>
      ) : (
        <>
          <p className="text-[#52635B] text-sm">If the admin or a relative already added you, claim that profile instead of creating a new one. 3 family approvals confirm it, then you can edit it yourself.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 h-11 rounded-xl border border-[#E5DEC9] bg-white px-3 text-sm" data-testid="claim-person-select">
              <option value="">{t("Choose your profile")}</option>
              {open.map((p) => <option key={p.person_id} value={p.person_id}>{p.name}{years(p) ? ` (${years(p)})` : ""}</option>)}
            </select>
            <Button onClick={claim} disabled={!pick} className="rounded-full bg-[#D96B43] hover:bg-[#C05731] h-11 px-6" data-testid="claim-submit-btn">{t("This is me")}</Button>
          </div>
        </>
      )}
    </section>
  );
};
